import React from 'react';

// Dependency-free Markdown renderer tuned for Claude output:
// headings, bold/italic/inline-code, links, ordered/unordered (nested) lists,
// tables, fenced code blocks, blockquotes, horizontal rules.

const COLORS = {
  text: '#1e293b',
  muted: '#475569',
  heading: '#0f172a',
  border: '#e2e8f0',
  codeBg: '#f1f5f9',
  codeText: '#be185d',
  blockBg: '#0f172a',
  blockText: '#e2e8f0',
  link: '#4f46e5',
  quoteBar: '#c7d2fe',
  tableHead: '#f8fafc',
};

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  // Tokenize: `code`, **bold**, *italic*, [text](url)
  const re = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*\n]+\*)|(\[[^\]]+\]\([^)\s]+\))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    const key = `${keyPrefix}-${i++}`;
    if (tok.startsWith('`')) {
      nodes.push(
        <code key={key} style={{
          backgroundColor: COLORS.codeBg, color: COLORS.codeText,
          padding: '1px 6px', borderRadius: 4, fontSize: '0.85em',
          fontFamily: "'SFMono-Regular', Consolas, monospace",
        }}>{tok.slice(1, -1)}</code>
      );
    } else if (tok.startsWith('**')) {
      nodes.push(<strong key={key} style={{ color: COLORS.heading, fontWeight: 650 }}>{renderInline(tok.slice(2, -2), key)}</strong>);
    } else if (tok.startsWith('[')) {
      const lm = tok.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
      if (lm) {
        nodes.push(
          <a key={key} href={lm[2]} target="_blank" rel="noopener noreferrer"
            style={{ color: COLORS.link, textDecoration: 'none', borderBottom: `1px solid ${COLORS.link}33` }}>
            {lm[1]}
          </a>
        );
      } else nodes.push(tok);
    } else if (tok.startsWith('*')) {
      nodes.push(<em key={key}>{tok.slice(1, -1)}</em>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

interface Block {
  type: 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'ul' | 'ol' | 'code' | 'quote' | 'hr' | 'table';
  text?: string;
  lang?: string;
  items?: { text: string; depth: number; ordered: boolean }[];
  rows?: string[][];
}

function parseBlocks(md: string): Block[] {
  const lines = String(md || '').replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim().startsWith('```')) {
      const lang = line.trim().slice(3).trim();
      const buf: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) { buf.push(lines[i]); i++; }
      i++; // closing fence
      blocks.push({ type: 'code', text: buf.join('\n'), lang });
      continue;
    }

    if (/^\s*\|.*\|\s*$/.test(line) && i + 1 < lines.length && /^\s*\|[\s:|-]+\|\s*$/.test(lines[i + 1])) {
      const rows: string[][] = [];
      const parseRow = (l: string) => l.trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim());
      rows.push(parseRow(line));
      i += 2;
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) { rows.push(parseRow(lines[i])); i++; }
      blocks.push({ type: 'table', rows });
      continue;
    }

    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) {
      blocks.push({ type: (['h1', 'h2', 'h3', 'h4'][h[1].length - 1]) as Block['type'], text: h[2] });
      i++;
      continue;
    }

    if (/^\s*([-*_])\s*\1\s*\1[\s\-*_]*$/.test(line) && line.trim().length >= 3) {
      blocks.push({ type: 'hr' });
      i++;
      continue;
    }

    if (/^\s*>/.test(line)) {
      const buf: string[] = [];
      while (i < lines.length && /^\s*>/.test(lines[i])) { buf.push(lines[i].replace(/^\s*>\s?/, '')); i++; }
      blocks.push({ type: 'quote', text: buf.join('\n') });
      continue;
    }

    const li = line.match(/^(\s*)([-*•]|\d+[.)])\s+(.*)$/);
    if (li) {
      const items: Block['items'] = [];
      while (i < lines.length) {
        const lm = lines[i].match(/^(\s*)([-*•]|\d+[.)])\s+(.*)$/);
        if (!lm) {
          // continuation line of previous item
          if (lines[i].trim() && /^\s{2,}/.test(lines[i]) && items.length) {
            items[items.length - 1].text += ' ' + lines[i].trim();
            i++;
            continue;
          }
          break;
        }
        items.push({ text: lm[3], depth: Math.min(Math.floor(lm[1].length / 2), 4), ordered: /\d/.test(lm[2]) });
        i++;
      }
      blocks.push({ type: items[0].ordered ? 'ol' : 'ul', items });
      continue;
    }

    if (!line.trim()) { i++; continue; }

    const buf: string[] = [line];
    i++;
    while (i < lines.length && lines[i].trim() &&
      !/^(#{1,4}\s|```|\s*>|\s*([-*•]|\d+[.)])\s|\s*\|.*\|\s*$)/.test(lines[i])) {
      buf.push(lines[i]); i++;
    }
    blocks.push({ type: 'p', text: buf.join(' ') });
  }
  return blocks;
}

const Markdown: React.FC<{ content: string; compact?: boolean }> = ({ content, compact }) => {
  const blocks = parseBlocks(content);
  const base = compact ? '0.84rem' : '0.92rem';

  return (
    <div style={{ color: COLORS.text, fontSize: base, lineHeight: 1.7, wordBreak: 'break-word' }}>
      {blocks.map((b, bi) => {
        const key = `b${bi}`;
        switch (b.type) {
          case 'h1':
          case 'h2':
            return (
              <div key={key} style={{
                fontSize: b.type === 'h1' ? '1.18em' : '1.1em', fontWeight: 700, color: COLORS.heading,
                margin: bi === 0 ? '0 0 8px' : '18px 0 8px',
                paddingBottom: 6, borderBottom: `1px solid ${COLORS.border}`,
              }}>{renderInline(b.text || '', key)}</div>
            );
          case 'h3':
          case 'h4':
            return (
              <div key={key} style={{
                fontSize: '1.02em', fontWeight: 650, color: COLORS.heading,
                margin: bi === 0 ? '0 0 6px' : '14px 0 6px',
              }}>{renderInline(b.text || '', key)}</div>
            );
          case 'hr':
            return <div key={key} style={{ borderBottom: `1px solid ${COLORS.border}`, margin: '14px 0' }} />;
          case 'quote':
            return (
              <div key={key} style={{
                borderLeft: `3px solid ${COLORS.quoteBar}`, paddingLeft: 12, margin: '10px 0',
                color: COLORS.muted, fontStyle: 'italic',
              }}>{renderInline(b.text || '', key)}</div>
            );
          case 'code':
            return (
              <pre key={key} style={{
                backgroundColor: COLORS.blockBg, color: COLORS.blockText, padding: '12px 14px',
                borderRadius: 10, overflow: 'auto', fontSize: '0.82em', margin: '10px 0',
                fontFamily: "'SFMono-Regular', Consolas, monospace", lineHeight: 1.55,
              }}><code>{b.text}</code></pre>
            );
          case 'ul':
          case 'ol': {
            return (
              <div key={key} style={{ margin: '6px 0' }}>
                {(b.items || []).map((it, ii) => (
                  <div key={`${key}-${ii}`} style={{
                    display: 'flex', gap: 8, paddingLeft: 6 + it.depth * 18, marginBottom: 4,
                  }}>
                    <span style={{ color: '#6366f1', flexShrink: 0, fontWeight: 600 }}>
                      {it.ordered
                        ? `${(b.items || []).slice(0, ii + 1).filter(x => x.depth === it.depth && x.ordered).length}.`
                        : it.depth > 0 ? '◦' : '•'}
                    </span>
                    <span style={{ flex: 1 }}>{renderInline(it.text, `${key}-${ii}`)}</span>
                  </div>
                ))}
              </div>
            );
          }
          case 'table': {
            const [head, ...body] = b.rows || [];
            return (
              <div key={key} style={{ overflowX: 'auto', margin: '10px 0' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.9em' }}>
                  <thead>
                    <tr>
                      {(head || []).map((c, ci) => (
                        <th key={ci} style={{
                          textAlign: 'left', padding: '8px 12px', backgroundColor: COLORS.tableHead,
                          border: `1px solid ${COLORS.border}`, fontWeight: 650, color: COLORS.heading,
                        }}>{renderInline(c, `${key}-h${ci}`)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {body.map((row, ri) => (
                      <tr key={ri}>
                        {row.map((c, ci) => (
                          <td key={ci} style={{ padding: '7px 12px', border: `1px solid ${COLORS.border}`, verticalAlign: 'top' }}>
                            {renderInline(c, `${key}-${ri}-${ci}`)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }
          default:
            return <p key={key} style={{ margin: '6px 0' }}>{renderInline(b.text || '', key)}</p>;
        }
      })}
    </div>
  );
};

export default Markdown;
